/**
 * Mode-aware data layer for the intake flow.
 *
 * - Mock mode: returns existing mock/local data (no Medplum calls).
 * - Live mode: reads from and writes to Medplum FHIR resources.
 *
 * Every public function accepts a MedplumClient so callers can use the
 * hook-provided instance. The `live` flag gates mock vs real behavior.
 */

import type { MedplumClient } from '@medplum/core';
import type {
  Appointment,
  Coverage,
  EpisodeOfCare,
  Patient,
  QuestionnaireResponse,
  Resource,
  ServiceRequest,
  Task,
} from '@medplum/fhirtypes';
import { getCoverageMemberId } from '@unify/fhir-model/resources/coverage';
import { toCoverageUpsertCommand } from '@unify/fhir-model/schemas';
import { intakeCoverageSchema } from '@/schemas/coverage.schema';
import { saveClientPortalCoverageViaBot } from '../../client-portal/services/portalCoverageUpdateBot';
import type { MockProvider } from './mockProviders';
import { MOCK_PROVIDERS } from './mockProviders';
import type { IntakeAnswers, TherapyBooking } from './types';
import { getTherapyTypesForSubmission, isHighRisk } from './types';
import type { IntakeLocation, BookableSlot } from './intakeFhirMapping';
import {
  practitionerToIntakeProvider,
  locationToIntakeLocation,
} from './intakeFhirMapping';
import { DEFAULT_PROVIDER_AVAILABILITY_TIMEZONE } from '../../../features/calendar/providerAvailabilitySchedule';
import {
  INTAKE_SCREENING_QUESTIONNAIRE_URL,
  INTAKE_SERVICE_TYPES,
  INTAKE_APPOINTMENT_STATUS,
} from './intakeFhirConstants';
import {
  computeReferralRequestMetrics,
  getReferralProviderPractitionerIdForIntake,
  isReferralRequestTask,
} from '../../../fhir-model/resources/referral-task';
import {
  REFERRAL_REQUEST_TASK_CODE,
  TASK_TYPE_SYSTEM,
} from '@unify/fhir-model/constants';
import { RBAC_PILOT_TAG_SYSTEM, RBAC_PILOT_TAG_CODE } from '../../../lib/caseloadFhirConstants';
import { collectPatientMetaAccounts } from '../../../lib/clientFacingTasks';
import { LOCATION_PHYSICAL_TYPE_SYSTEM } from '@unify/fhir-model/constants';
import { MOCK_LOCATIONS as MOCK_LOCATIONS_RAW } from './intakeLocationsMock';
import { intakeTherapyTypeToServiceTypeCode } from '../../../lib/serviceRequestFhirConstants';
import {
  createIntakeServiceRequestsViaBot,
  getProviderAvailability,
} from './intakeLeadPatient';
import type { SessionType } from './types';

// Re-export the mock locations for StepLocation to use in mock mode.
export { MOCK_LOCATIONS_RAW as MOCK_LOCATIONS };

// ---------------------------------------------------------------------------
// Providers
// ---------------------------------------------------------------------------

export interface IntakeProvidersResult {
  providers: MockProvider[];
  /** Practitioner id → open referral-request Task id (for reservation). */
  providerTaskMap: Record<string, string>;
}

export async function listIntakeProviders(opts: {
  live: boolean;
  medplum: MedplumClient;
}): Promise<IntakeProvidersResult> {
  if (!opts.live) {
    return { providers: [...MOCK_PROVIDERS], providerTaskMap: {} };
  }

  const referralCode = `${TASK_TYPE_SYSTEM}|${REFERRAL_REQUEST_TASK_CODE}`;
  const tasks = await opts.medplum.searchResources('Task', {
    code: referralCode,
    _count: '200',
  });

  const openTasks = (tasks as Task[]).filter((t) => {
    if (!isReferralRequestTask(t)) return false;
    const status = t.status;
    return status === 'requested' || status === 'in-progress';
  });

  const providerTaskMap: Record<string, string> = {};
  const practitionerIds = new Set<string>();

  for (const task of openTasks) {
    if (!task.id) continue;
    const metrics = computeReferralRequestMetrics(task);
    if (metrics.availableSlots <= 0) continue;

    // PRD: requester = provider asking for clients; owner = intake org (see useReferralTasks).
    const practitionerId = getReferralProviderPractitionerIdForIntake(task);
    if (!practitionerId) continue;

    practitionerIds.add(practitionerId);
    if (!providerTaskMap[practitionerId]) {
      providerTaskMap[practitionerId] = task.id;
    }
  }

  if (practitionerIds.size === 0) {
    return { providers: [], providerTaskMap: {} };
  }

  const practitioners = await opts.medplum.searchResources('Practitioner', {
    _id: [...practitionerIds].join(','),
    _count: String(practitionerIds.size),
  });

  const providers = practitioners.map(practitionerToIntakeProvider);
  const filteredTaskMap: Record<string, string> = {};
  for (const p of providers) {
    const taskId = providerTaskMap[p.id];
    if (taskId) filteredTaskMap[p.id] = taskId;
  }

  return { providers, providerTaskMap: filteredTaskMap };
}

export async function getProviderProfile(opts: {
  live: boolean;
  medplum: MedplumClient;
  providerId: string;
}): Promise<MockProvider | null> {
  if (!opts.live) {
    return MOCK_PROVIDERS.find((p) => p.id === opts.providerId) ?? null;
  }

  try {
    const p = await opts.medplum.readResource('Practitioner', opts.providerId);
    return practitionerToIntakeProvider(p);
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Locations
// ---------------------------------------------------------------------------

export async function listIntakeLocations(opts: {
  live: boolean;
  medplum: MedplumClient;
  /** When present, filter locations to this Organization id. */
  organizationId?: string;
}): Promise<IntakeLocation[]> {
  if (!opts.live) {
    return MOCK_LOCATIONS_RAW;
  }

  const searchParams: Record<string, string> = { status: 'active', _count: '100' };
  if (opts.organizationId) {
    searchParams['organization'] = `Organization/${opts.organizationId}`;
  }

  const locations = await opts.medplum.searchResources('Location', searchParams);

  return locations
    .filter((loc) =>
      !loc.physicalType?.coding?.some(
        (c) => c.system === LOCATION_PHYSICAL_TYPE_SYSTEM && c.code === 'ro',
      ),
    )
    .map(locationToIntakeLocation);
}

// ---------------------------------------------------------------------------
// Slots / availability
// ---------------------------------------------------------------------------

const DAY_CODE_TO_JS_DAY: Record<string, number> = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
};

function isoSlotToBookable(slot: { start: string; end: string }, dateKey: string): BookableSlot {
  const timeMatch = slot.start.match(/T(\d{2}):(\d{2})/);
  const timeKey = timeMatch ? `${timeMatch[1]}:${timeMatch[2]}` : '09:00';
  return {
    slotId: slot.start,
    start: slot.start,
    end: slot.end,
    timeKey,
    dateKey,
  };
}

export interface ProviderAvailabilityForDate {
  slots: BookableSlot[];
  availableWeekdays: string[];
  timeZone: string;
}

/** Fetch computed availability for one provider/day via intake-lead-patient bot. */
export async function fetchProviderAvailabilityForDate(opts: {
  live: boolean;
  medplum: MedplumClient;
  providerId: string;
  sessionType: SessionType;
  date: string;
}): Promise<ProviderAvailabilityForDate> {
  if (!opts.live) {
    return { slots: [], availableWeekdays: [], timeZone: DEFAULT_PROVIDER_AVAILABILITY_TIMEZONE };
  }

  const sessionType = opts.sessionType === 'consultation' ? 'consultation' : 'intake';
  const result = await getProviderAvailability(opts.medplum, {
    providerId: opts.providerId,
    sessionType,
    date: opts.date,
  });

  const slots = Array.isArray(result?.slots) ? result.slots : [];

  return {
    slots: slots.map((s) => isoSlotToBookable(s, opts.date)),
    availableWeekdays: Array.isArray(result?.availableWeekdays) ? result.availableWeekdays : [],
    timeZone: result?.timeZone ?? DEFAULT_PROVIDER_AVAILABILITY_TIMEZONE,
  };
}

/** YYYY-MM-DD keys in [minDate, maxDate] that fall on provider working weekdays. */
export function bookableDateKeysInWindow(
  minDate: Date,
  maxDate: Date,
  availableWeekdays: string[],
): string[] {
  const allowed = new Set(
    availableWeekdays.map((code) => DAY_CODE_TO_JS_DAY[code]).filter((n) => n !== undefined),
  );
  if (allowed.size === 0) return [];

  const keys: string[] = [];
  const cursor = new Date(minDate);
  cursor.setHours(0, 0, 0, 0);
  const end = new Date(maxDate);
  end.setHours(23, 59, 59, 999);

  while (cursor <= end) {
    if (allowed.has(cursor.getDay())) {
      const y = cursor.getFullYear();
      const m = String(cursor.getMonth() + 1).padStart(2, '0');
      const d = String(cursor.getDate()).padStart(2, '0');
      keys.push(`${y}-${m}-${d}`);
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return keys;
}

// ---------------------------------------------------------------------------
// Intake submission — create Patient, screening QuestionnaireResponse, Appointment(s)
// ---------------------------------------------------------------------------

export interface IntakeSubmissionResult {
  patient: Patient;
  questionnaireResponse: QuestionnaireResponse;
  appointments: Appointment[];
  coverage?: Coverage;
  /** One ServiceRequest + paired EpisodeOfCare per selected therapy type. */
  serviceRequests: { serviceRequest: ServiceRequest; episodeOfCare: EpisodeOfCare }[];
}

export async function submitIntake(opts: {
  live: boolean;
  medplum: MedplumClient;
  answers: IntakeAnswers;
  providers: MockProvider[];
  /** Practice Organization id — sets ServiceRequest.performer (not Patient org extension). */
  organizationId?: string;
  /** When true, FHIR draft intake was already promoted (SR/EoC + referral hold); skip duplicate SR/appt creation. */
  reservationPromoted?: boolean;
  /**
   * Draft/promoted EpisodeOfCare ids from the reservation flow (forwarded for future use).
   * Insurance is patient-level Coverage only — not linked per episode.
   */
  episodeOfCareIds?: string[];
}): Promise<IntakeSubmissionResult | null> {
  if (!opts.live) return null;

  const { medplum, answers, providers } = opts;

  const demographics = {
    name: [{ given: [answers.firstName], family: answers.lastName }],
    telecom: [{ system: 'email' as const, value: answers.email }],
    birthDate: normalizeDate(answers.dateOfBirth),
    address: answers.zip ? [{ postalCode: answers.zip }] : undefined,
    active: true as const,
    communication: answers.language
      ? [
          {
            language: {
              coding: [{ system: 'urn:ietf:bcp:47', code: answers.language }],
            },
          },
        ]
      : undefined,
  };

  let patient: Patient;
  const leadPatientId = answers.intakeLeadPatientId?.trim();
  if (opts.reservationPromoted && leadPatientId) {
    // Patient was created/updated by intake-lead-patient bot; public OAuth client often cannot read/update Patient by id.
    patient = { resourceType: 'Patient', id: leadPatientId, ...demographics };
  } else if (leadPatientId) {
    try {
      const existing = await medplum.readResource('Patient', leadPatientId);
      patient = await medplum.updateResource<Patient>({
        ...existing,
        ...demographics,
      });
    } catch (err) {
      throw new Error(
        'Could not access your intake patient record. Please complete email verification or contact support.',
        { cause: err },
      );
    }
  } else {
    throw new Error(
      'Intake submission requires a verified patient ID. Complete email verification first.',
    );
  }

  // 2–3. Screening QuestionnaireResponse + optional Coverage
  const patientId = patient.id as string;
  const reservationPromoted = opts.reservationPromoted === true;
  const qr = await createScreeningQuestionnaireResponse(medplum, { patientId, answers });
  let coverage: Coverage | undefined;
  if (answers.payType === 'insurance' && answers.insurancePayerOrgId) {
    const parsed = intakeCoverageSchema.parse({
      patientId,
      payerOrganizationId: answers.insurancePayerOrgId,
      payorName: answers.insuranceCompany,
      memberId: answers.memberId,
      groupNumber: answers.groupId || undefined,
      subscriberRelationship: answers.subscriberRelationship,
      subscriberFirstName: answers.subscriberFirstName || undefined,
      subscriberLastName: answers.subscriberLastName || undefined,
      subscriberDateOfBirth: answers.subscriberDateOfBirth || undefined,
      periodStart: answers.insuranceStartDate || undefined,
      periodEnd: answers.insuranceEndDate || undefined,
    });

    coverage = await upsertIntakeCoverageViaPortalBot(medplum, {
      patientId,
      payerOrgId: parsed.payerOrganizationId,
      payerName: parsed.payorName,
      memberId: parsed.memberId,
      groupNumber: parsed.groupNumber,
      relationship: parsed.subscriberRelationship,
      subscriberFirstName: parsed.subscriberFirstName,
      subscriberLastName: parsed.subscriberLastName,
      subscriberDateOfBirth: parsed.subscriberDateOfBirth,
      periodStart: parsed.periodStart,
      periodEnd: parsed.periodEnd,
    });
  }

  // 4. Create Appointments for each non-cancelled booking
  const appointments: Appointment[] = [];
  if (!reservationPromoted) {
    for (const booking of answers.bookings) {
      if (booking.cancelled) continue;
      const appt = await createIntakeAppointment({
        medplum,
        patient,
        booking,
        answers,
        providers,
      });
      if (appt) appointments.push(appt);
    }
  }

  // 5. One ServiceRequest + paired EpisodeOfCare per selected (non-cancelled)
  let serviceRequests: IntakeSubmissionResult['serviceRequests'] = [];
  if (!reservationPromoted) {
    const activeTherapyTypes = getTherapyTypesForSubmission(answers);
    const waitlistedTypes = answers.waitlistedTherapyTypes ?? [];
    /** Group waitlist (PRD §8): closed-cohort / no capacity → tracker-stage waitlist. */
    const groupWaitlisted =
      activeTherapyTypes.includes('group') && waitlistedTypes.includes('group');
    const stage = groupWaitlisted
      ? 'waitlist'
      : isHighRisk(answers)
        ? 'screening'
        : answers.payType === 'cash'
          ? 'matching'
          : 'needs-verification';
    /** Pilot-tagged SR + EoC pairs via intake-lead-patient Bot (OAuth client lacks ServiceRequest write). */
    const { pairs } = await createIntakeServiceRequestsViaBot(medplum, {
      patientId: patient.id as string,
      organizationId: opts.organizationId,
      therapyTypeCodes: activeTherapyTypes.map(intakeTherapyTypeToServiceTypeCode),
      topics: answers.topics,
      approaches: answers.specialties,
      providerPreferences: answers.demographicPrefs,
      availabilityTimeOfDay: answers.availabilityTimeOfDay,
      availabilityDays: answers.availabilityDays,
      sessionFormat: answers.format,
      preferredLocationIds: answers.preferredLocationIds,
      paymentType: intakePayTypeToServiceRequestCoding(answers.payType),
      coverageId: coverage?.id,
      stage,
    });
    serviceRequests = pairs;
  }

  return { patient, questionnaireResponse: qr, appointments, coverage, serviceRequests };
}

export { createIntakeLeadPatient } from './intakeLeadPatient';

// ---------------------------------------------------------------------------
// Single appointment creation
// ---------------------------------------------------------------------------

export async function createIntakeAppointment(opts: {
  medplum: MedplumClient;
  patient: Patient;
  booking: TherapyBooking;
  answers: IntakeAnswers;
  providers: MockProvider[];
}): Promise<Appointment | null> {
  const { medplum, patient, booking, answers, providers } = opts;
  if (!booking.bookingDate || !booking.bookingTime) return null;

  const isConsultation = booking.sessionType === 'consultation';
  const durationMinutes = isConsultation ? 15 : 50;
  const start = new Date(`${booking.bookingDate}T${booking.bookingTime}:00`);
  const end = new Date(start.getTime() + durationMinutes * 60_000);

  const serviceType = isConsultation
    ? INTAKE_SERVICE_TYPES.consultation
    : INTAKE_SERVICE_TYPES.intake;

  const provider = providers.find((p) => p.id === booking.selectedProviderId);

  const participants: Appointment['participant'] = [
    {
      actor: {
        reference: `Patient/${patient.id}`,
        display: `${answers.firstName} ${answers.lastName}`,
      },
      status: 'accepted',
    },
  ];

  if (booking.selectedProviderId) {
    participants.push({
      actor: {
        reference: `Practitioner/${booking.selectedProviderId}`,
        display: provider?.name ?? 'Provider',
      },
      status: 'accepted',
    });
  }

  const careTeamAccounts = patient.id ? collectPatientMetaAccounts(patient) : [];

  const appointmentBody = stampResource({
    resourceType: 'Appointment',
    status: INTAKE_APPOINTMENT_STATUS,
    description: `${booking.therapyType} ${isConsultation ? 'consultation' : 'intake session'}`,
    start: start.toISOString(),
    end: end.toISOString(),
    serviceType: [
      {
        coding: [serviceType],
        text: serviceType.display,
      },
    ],
    participant: participants,
    ...(patient.id
      ? {
          meta: {
            account: { reference: `Patient/${patient.id}` },
            ...(careTeamAccounts.length > 0 ? { accounts: careTeamAccounts } : {}),
          },
        }
      : {}),
  });

  return medplum.createResource<Appointment>(appointmentBody);
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function stampResource<T extends Resource>(resource: T): T {
  return {
    ...resource,
    meta: {
      ...resource.meta,
      tag: [{ system: RBAC_PILOT_TAG_SYSTEM, code: RBAC_PILOT_TAG_CODE }],
    },
  };
}

function normalizeDate(input: string): string {
  // Accept MM/DD/YYYY and convert to YYYY-MM-DD
  const slashMatch = input.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (slashMatch) {
    return `${slashMatch[3]}-${slashMatch[1]}-${slashMatch[2]}`;
  }
  return input;
}

function intakePayTypeToServiceRequestCoding(
  pay: IntakeAnswers['payType'],
): 'insurance' | 'self-pay' | undefined {
  if (pay === 'insurance') return 'insurance';
  if (pay === 'cash') return 'self-pay';
  return undefined;
}

/** True when an active Coverage already has the same payer org + member id. */
export function coverageMatchesIntakePayerAndMember(
  coverage: Coverage,
  payerOrgId: string,
  memberId: string,
): boolean {
  const payorRef = coverage.payor?.[0]?.reference ?? '';
  const orgId = payorRef.startsWith('Organization/')
    ? payorRef.slice('Organization/'.length)
    : payorRef;
  const existingMember = getCoverageMemberId(coverage)?.trim();
  return (
    orgId === payerOrgId.trim() &&
    Boolean(existingMember) &&
    existingMember === memberId.trim()
  );
}

/**
 * Upsert intake insurance via the portal coverage bot (Patient Self Policy cannot write
 * Coverage/RelatedPerson). Reuses an existing active Coverage when payer + member id match.
 */
export async function upsertIntakeCoverageViaPortalBot(
  medplum: MedplumClient,
  input: {
    patientId: string;
    payerOrgId: string;
    payerName: string;
    memberId: string;
    groupNumber?: string;
    relationship?: string;
    subscriberFirstName?: string;
    subscriberLastName?: string;
    subscriberDateOfBirth?: string;
    periodStart?: string;
    periodEnd?: string;
  },
): Promise<Coverage> {
  const active = await medplum.searchResources('Coverage', {
    beneficiary: `Patient/${input.patientId}`,
    status: 'active',
    _count: '10',
  });
  const match = active.find((c) =>
    coverageMatchesIntakePayerAndMember(c, input.payerOrgId, input.memberId),
  );
  if (match?.id) {
    return match;
  }

  const command = toCoverageUpsertCommand({
    patientId: input.patientId,
    priority: 'primary',
    payerOrgId: input.payerOrgId,
    memberId: input.memberId,
    groupNumber: input.groupNumber,
    relationship: input.relationship,
    subscriberFirstName: input.subscriberFirstName,
    subscriberLastName: input.subscriberLastName,
    subscriberDateOfBirth: input.subscriberDateOfBirth,
    periodStart: input.periodStart,
    periodEnd: input.periodEnd,
  });

  const saved = await saveClientPortalCoverageViaBot(medplum, command);

  return medplum.readResource('Coverage', saved.coverageId);
}

/**
 * Create the screening QuestionnaireResponse for a patient. Shared by the standard intake
 * submission and the high-risk booking path so the six screening answers are always persisted.
 */
export async function createScreeningQuestionnaireResponse(
  medplum: MedplumClient,
  opts: { patientId: string; answers: IntakeAnswers },
): Promise<QuestionnaireResponse> {
  const { patientId, answers } = opts;
  return medplum.createResource<QuestionnaireResponse>(
    stampResource({
      resourceType: 'QuestionnaireResponse',
      questionnaire: INTAKE_SCREENING_QUESTIONNAIRE_URL,
      status: 'completed',
      subject: {
        reference: `Patient/${patientId}`,
        display: `${answers.firstName} ${answers.lastName}`,
      },
      authored: new Date().toISOString(),
      item: buildScreeningQuestionnaireItems(answers),
    }),
  );
}

function buildScreeningQuestionnaireItems(answers: IntakeAnswers): QuestionnaireResponse['item'] {
  const item = (
    linkId: string,
    text: string,
    value: boolean | null,
  ): NonNullable<QuestionnaireResponse['item']>[number] => ({
    linkId,
    text,
    answer: value != null ? [{ valueBoolean: value }] : [],
  });

  return [
    item('screening-emergency', 'Emergency screening', answers.screeningEmergency),
    item('screening-hospitalization', 'Hospitalization screening', answers.screeningHospitalization),
    item('screening-suicidal-ideation', 'Suicidal ideation screening', answers.screeningSuicidalIdeation),
    item('screening-medications', 'Medications screening', answers.screeningMedications),
    item('screening-substance-use', 'Substance use screening', answers.screeningSubstanceUse),
    item('screening-legal', 'Legal proceedings screening', answers.screeningLegalProceedings),
  ];
}
